"use client";

import { rmcClient } from "@/lib/rmcClient";
import { DOC_SLOTS, getDocUrl, pickLatestBankAccount, resolveAssetHref } from "@/lib/driverKyc";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useReactToPrint } from "react-to-print";

function pick(...values) {
  for (const v of values) {
    if (v !== undefined && v !== null && String(v).trim() !== "") return String(v);
  }
  return "";
}

function hasValue(v) {
  return v !== undefined && v !== null && String(v).trim() !== "";
}

function slotByCode(code) {
  return DOC_SLOTS.find((s) => s.code === code);
}

function isLikelyImageUrl(url) {
  if (!url || typeof url !== "string") return false;
  return /\.(jpe?g|png|gif|webp)(\?|$)/i.test(url);
}

function PrintDocBox({ title, subtitle, url, requiredStar }) {
  const abs = url ? resolveAssetHref(url) : null;
  const showImg = abs && isLikelyImageUrl(abs);
  const isPdf = abs && /\.pdf(\?|$)/i.test(abs);

  return (
    <div className="flex min-h-[34mm] flex-col border border-slate-600 bg-white p-1">
      {subtitle ? (
        <p className="mb-0.5 text-center text-[7px] leading-tight text-slate-600">{subtitle}</p>
      ) : null}
      <p className="text-[8px] font-semibold leading-tight text-slate-900">
        {requiredStar ? "* " : ""}
        {title}
      </p>
      <div className="flex min-h-[26mm] flex-1 items-center justify-center overflow-hidden">
        {!abs ? (
          <span className="text-[8px] text-slate-400">—</span>
        ) : showImg ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={abs} alt={title} className="max-h-[26mm] w-full object-contain" />
        ) : isPdf ? (
          <div className="px-0.5 text-center">
            <p className="text-[7px] font-medium text-slate-700">PDF</p>
            <p className="line-clamp-2 break-all text-[6px] leading-tight text-slate-500">{abs}</p>
          </div>
        ) : (
          <p className="line-clamp-2 break-all px-0.5 text-center text-[6px] text-slate-600">{abs}</p>
        )}
      </div>
    </div>
  );
}

const DriverKycPrint = forwardRef(function DriverKycPrint({ driver }, ref) {
  const contentRef = useRef(null);
  const [company, setCompany] = useState(null);
  const [freshDriver, setFreshDriver] = useState(null);

  useEffect(() => {
    (async () => {
      const c = (await rmcClient.getCompany()).data;
      setCompany(c);
    })();
  }, []);

  const printFn = useReactToPrint({
    contentRef,
    documentTitle: driver?.employee_id
      ? `driver-kyc-${driver.employee_id}`
      : `driver-kyc-${driver?.id ?? "detail"}`,
  });

  const prepareFreshDriver = async () => {
    if (!driver?.id) return;
    const full = (await rmcClient.getDriver(driver.id)).data;
    flushSync(() => {
      setFreshDriver(full);
    });
  };

  useImperativeHandle(ref, () => ({
    openPrint: async () => {
      if (!driver?.id) return;
      await prepareFreshDriver();
      await new Promise((resolve) => setTimeout(resolve, 0));
      printFn();
    },
  }));

  useEffect(() => {
    setFreshDriver(null);
  }, [driver?.id]);

  if (!driver) {
    return <div ref={contentRef} className="hidden" aria-hidden />;
  }

  const d = freshDriver || driver;

  const companyName = pick(company?.name, company?.company_name) || "Company Name";
  const companyAddress = pick(company?.address, company?.address_line1);
  const companyPhone = pick(
    company?.contact_phone,
    company?.mobile_no,
    company?.mobile,
    company?.mobile_number,
    company?.phone,
    company?.contact_no,
  );
  const companyGst = pick(company?.gst_number, company?.gstin);
  const companyEmail = pick(company?.email, company?.contact_email);
  const companyBlessing = pick(
    company?.blessing,
    company?.invoice_blessing,
    "Shree Ganeshay Namah",
  );

  const bank = pickLatestBankAccount(d.bank_accounts);
  const bankLines = bank
    ? [
        pick(bank.bank_name),
        [pick(bank.account_type), pick(bank.account_no)].filter(Boolean).join(" · "),
        [pick(bank.ifsc_code), pick(bank.branch)].filter(Boolean).join(" · "),
      ].filter((line) => hasValue(line))
    : [];

  const panSlot = slotByCode("PAN");
  const licF = slotByCode("LICENCE_FRONT");
  const licB = slotByCode("LICENCE_BACK");
  const aadF = slotByCode("AADHAR_FRONT");
  const aadB = slotByCode("AADHAR_BACK");
  const pfSlot = slotByCode("PF_CERTIFICATE");

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed left-0 top-0 -z-10 h-0 w-0 overflow-hidden opacity-0 print:static print:z-50 print:h-auto print:w-auto print:overflow-visible print:opacity-100"
    >
      <div ref={contentRef} className="bg-white text-black print:block">
        <section className="mx-auto box-border flex h-[297mm] max-h-[297mm] w-[210mm] flex-col overflow-hidden border border-slate-700 p-1.5 text-[9px] leading-tight print:break-inside-avoid">
          {hasValue(companyBlessing) && (
            <div className="mb-1 text-center text-[9px] italic leading-none text-slate-700">
              {companyBlessing}
            </div>
          )}

          <div className="shrink-0 border border-slate-600">
            <div className="px-2 py-1 text-center">
              <p className="text-[8px] font-semibold uppercase tracking-wide text-slate-800">
                Driver KYC
              </p>
              <p className="text-sm font-bold leading-tight">{companyName}</p>
              {hasValue(companyAddress) && (
                <p className="text-[9px] leading-snug text-slate-700">{companyAddress}</p>
              )}
              {(hasValue(companyPhone) || hasValue(companyEmail) || hasValue(companyGst)) && (
                <p className="text-[9px] leading-snug text-slate-700">
                  {hasValue(companyPhone) && `Mobile: ${companyPhone}`}
                  {hasValue(companyPhone) && hasValue(companyEmail) && " | "}
                  {hasValue(companyEmail) && `Email: ${companyEmail}`}
                  {(hasValue(companyPhone) || hasValue(companyEmail)) && hasValue(companyGst) && " | "}
                  {hasValue(companyGst) && `GSTIN: ${companyGst}`}
                </p>
              )}
            </div>
          </div>

          <div className="mt-1.5 flex shrink-0 gap-2 border border-slate-600 p-1.5">
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex gap-1.5 leading-snug">
                <span className="min-w-[76px] shrink-0 font-semibold text-slate-800">Name</span>
                <span>: {pick(d.name) || "—"}</span>
              </div>
              <div className="flex gap-1.5 leading-snug">
                <span className="min-w-[76px] shrink-0 font-semibold text-slate-800">Address</span>
                <span>: {pick(d.address) || "—"}</span>
              </div>
              <div className="flex gap-1.5 leading-snug">
                <span className="min-w-[76px] shrink-0 font-semibold text-slate-800">Employee ID</span>
                <span>: {pick(d.employee_id) || "—"}</span>
              </div>
              <div className="flex gap-1.5 leading-snug">
                <span className="min-w-[76px] shrink-0 font-semibold text-slate-800">Phone No.</span>
                <span>: {pick(d.contact_no, d.contact) || "—"}</span>
              </div>
              <div className="pt-0.5">
                <p className="font-semibold text-slate-800">Bank detail {!bank ? "" : "(latest)"}</p>
                {bankLines.length > 0 ? (
                  <div className="mt-0.5 border-l-2 border-slate-400 pl-1.5 text-[9px] leading-snug text-slate-800">
                    {bankLines.map((line, i) => (
                      <p key={i}>{line}</p>
                    ))}
                  </div>
                ) : (
                  <p className="mt-0.5 text-[9px] text-slate-500">—</p>
                )}
              </div>
            </div>
            <div className="shrink-0 self-start text-[9px] italic leading-snug text-slate-600">
              (Driver KYC Details)
            </div>
          </div>

          {/* Six cells on 3×2 grid — no blank placeholder; PF uses bottom-right when present */}
          <div className="mt-1.5 grid shrink-0 grid-cols-2 gap-1">
            <PrintDocBox
              title="Pancard"
              subtitle="for → EPFO"
              url={panSlot ? getDocUrl(d, panSlot) : null}
            />
            <PrintDocBox
              title="Driving Licence"
              url={licF ? getDocUrl(d, licF) : null}
              requiredStar
            />
            <PrintDocBox title="Driving Licence (back)" url={licB ? getDocUrl(d, licB) : null} />
            <PrintDocBox title="Aadhaar Card" url={aadF ? getDocUrl(d, aadF) : null} requiredStar />
            <PrintDocBox title="Aadhaar Card (back)" url={aadB ? getDocUrl(d, aadB) : null} />
            <PrintDocBox
              title="PF certificate (EPFO)"
              url={pfSlot ? getDocUrl(d, pfSlot) : null}
            />
          </div>
        </section>
      </div>
    </div>
  );
});

DriverKycPrint.displayName = "DriverKycPrint";

export default DriverKycPrint;
